import { JOURNEY, journeyIndex } from '../lib/format';

export type JourneyTab = 'overview' | 'documents' | 'rating' | 'partners' | 'funding';

const STEP_TAB: Record<(typeof JOURNEY)[number]['key'], JourneyTab> = {
  INTAKE: 'overview',
  SUBMITTED: 'documents',
  RATED: 'rating',
  OUTREACH: 'partners',
  FUNDED: 'funding',
};

const TAB_STEP: Partial<Record<JourneyTab | 'ready', number>> = {
  overview: 0,
  documents: 1,
  rating: 2,
  ready: 2,
  partners: 3,
  funding: 4,
};

export default function JourneyRail({
  status,
  viewingTab,
  onStepClick,
  lockedTabs,
}: {
  status?: string | null;
  /** Which workspace tab is open — drives the highlighted step background. */
  viewingTab?: JourneyTab | 'ready' | 'overview';
  /** Jump to the workspace tab for that journey step. */
  onStepClick?: (tab: JourneyTab) => void;
  /** Tabs that cannot be opened yet (e.g. Partners / Funded before customer confirm). */
  lockedTabs?: JourneyTab[];
}) {
  const statusIdx = journeyIndex(status);
  const viewingIdx = viewingTab != null ? (TAB_STEP[viewingTab] ?? -1) : -1;
  const locked = new Set(lockedTabs || []);

  return (
    <ol className="grid grid-cols-5 gap-2">
      {JOURNEY.map((step, i) => {
        const done = i < statusIdx;
        const next = i === statusIdx;
        const viewing = i === viewingIdx;
        const tab = STEP_TAB[step.key];
        const isLocked = locked.has(tab);
        const clickable = Boolean(onStepClick) && !isLocked;
        const className = 'w-full rounded-xl px-2 py-2.5 text-center transition';
        const style = {
          background: isLocked ? '#f8fafc' : viewing ? '#d6006e' : done ? '#ecfdf5' : next ? '#fff5f8' : '#ffffff',
          border: `1px solid ${isLocked ? '#e2e8f0' : viewing ? '#d6006e' : done ? '#a7f3d0' : next ? '#f9a8d4' : '#fce7f3'}`,
          boxShadow: !isLocked && viewing ? '0 8px 22px rgba(214,0,110,0.28)' : undefined,
          opacity: isLocked ? 0.55 : 1,
        } as const;
        const labelTone = isLocked ? 'text-slate-400' : viewing ? 'text-white/75' : done ? 'text-emerald-700' : 'text-[#c084a0]';
        const titleTone = isLocked ? 'text-slate-500' : viewing ? 'text-white' : 'text-[#6b0030]';
        const body = (
          <>
            <p className={`text-[10px] font-bold uppercase tracking-wider ${labelTone}`}>
              {isLocked ? 'Locked' : done ? '✓ Done' : next ? `Step ${i + 1} · Next` : `Step ${i + 1}`}
            </p>
            <p className={`mt-0.5 text-xs font-bold ${titleTone}`}>{step.label}</p>
            {viewing && !isLocked && <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/80">Viewing</p>}
          </>
        );
        return (
          <li key={step.key}>
            {clickable ? (
              <button
                type="button"
                onClick={() => onStepClick?.(tab)}
                className={`${className} hover:brightness-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d6006e]`}
                style={style}
                aria-current={viewing ? 'step' : undefined}
                title={`Open ${step.label}`}
              >
                {body}
              </button>
            ) : (
              <div
                className={className}
                style={style}
                title={isLocked ? 'Available after the customer approves partner sharing' : undefined}
                aria-disabled={isLocked || undefined}
              >
                {body}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
